import { EncounterSubmissionStatusEnum } from '../../Enum/EncounterSubmissionStatusEnum';
import { MeansOfEngagementEnum } from '../../Enum/MeansOfEngagementEnum';
import { MemberStatusEnum } from '../../Enum/MemberStatusEnum';
import { PatientOutreachOrInitiatedTypeEnum } from '../../Enum/PatientOutreachOrInitiatedTypeEnum';
import { StatusEnum } from '../../Enum/StatusEnum';
import { DocumentEngagementType } from '../../Enum/DocumentEngagementTypeEnum';
import BaseEntity from '../BaseEntity.model';
import { DiagnosticStatus } from '../Assessment/DiagnosticStatus.model';
import { Medications } from '../Medications/Medications.model';
import { CareCircleConfirmation } from '../Member/CareCircleConfirmation.model';
import { MemberEncounterDraft } from '../Member/MemberEncounterDraft.model';
import { EngagedProvider } from './EngagedProvider.model';
import { ProviderRole, ReasonForVisit, RequestReviewStatus, StaffReferral, TelemedicineDetails } from './EngagementDraft.model';
import { SummaryNote } from './SummaryNote.model';
import { DraftWorkflowState } from './DraftWorkflowState.model';

export default interface IDocumentEngagement extends BaseEntity {
    MeansOfEngagement: MeansOfEngagementEnum;
    MemberId: string;
    EngagedProvider: EngagedProvider;
    Notes: string;
    CreationTime: string;
    VisitedTime: string;
    ReasonForVisit: ReasonForVisit;
    StaffReferral: StaffReferral;
    PatientOutreachOrInitiatedType: PatientOutreachOrInitiatedTypeEnum;
    UpdationTime: string;
    UpdatedProvider: EngagedProvider;
    EncounterSubmissionStatus: EncounterSubmissionStatusEnum;
    Contacted: boolean;
    ContactResult: string;
    AvoidedER: boolean;
    TelemedicineDetails: TelemedicineDetails;
    SdohAssessmentId: string;
    VitalsignAssessmentId: string;
    PhqAssessmentId: string;
    PastPresentHistoryId: string;
    PastSurgicalHistoryId: string;
    ReviewSystemId: string;
    PhysicalExamId: string;
    HgbA1CAssessmentId: string;
    LipidAssessmentId: string;
    CounselingAssessmentId: string;
    DiabetesFootAssessmentId: string;
    UrinalysisReportId: string;
    MiniMentalExamId: string;
    SuicidalHomicidalIdeationId: string;
    FactorAndRiskAssessmentId: string;
    SubjectiveAssessmentId: string;
    SummaryNotes: SummaryNote[];
    ProgressNotes: string;
    FollowupRecommendations: string;
    CompletedRequestIds: string[];
    InProgressRequestIds: string[];
    ActiveMedications: Medications[];
    AppointmentIds: string[];
    AssociatedAppointmentId: string;
    FollowUpRequestIds: string[];
    EducationSessionIds: string[];
    AssessedRole: ProviderRole;
    RequestReviewStatus: RequestReviewStatus;
    ConsentFormId: string;
    BudgetFormId: string;
    TransitionalCareId: string;
    AdlAssessmentId: string;
    SexualHealthId: string;
    FallRiskId: string;
    VisionAndHearingId: string;
    AdvancedDirectiveId: string;
    TobaccoAndDrugAssessmentId: string;
    PreventativeHealthAdviceId: string;
    HraEducationId: string;
    MemberName: string;
    HasClaim: boolean;
    CanReopen?: boolean;
    CareCircleConfirmation: CareCircleConfirmation;
    StatementOfCare: string;

    // New fields for draft/finalized lifecycle
    Type?: DocumentEngagementType;
    DraftWorkflowState?: DraftWorkflowState;
    Encounters?: MemberEncounterDraft[];

    // Additional assessment IDs (from EngagementDraft)
    IsShowStaffReferral?: boolean;
    isShowPatientOutReach?: boolean;
    ScreeningFuhFumId?: string;
    ChpFullProgramId?: string;
    HamiltonAnxietyScaleId?: string;
    AbuseHistoryId?: string;
    LegalHistoryId?: string;
    CulturalConsiderationsId?: string;
    DevelopmentalHistoryId?: string;
    EatingDisorderId?: string;
    MentalHealthTreatmentHistoryId?: string;
    CurrentNeedsId?: string;
    GAFScaleId?: string;
    AdditionalCHPCriteriaId?: string;

    // Status fields (flat access for backward compat with EngagementDraft callers)
    SdohAssessmentStatus?: StatusEnum;
    MiniMentalExamStatus?: StatusEnum;
    SuicidalHomicidalIdeationStatus?: StatusEnum;
    VitalSignStatus?: StatusEnum;
    HamiltonAnxietyScaleStatus?: StatusEnum;
    AbuseHistoryStatus?: StatusEnum;
    LegalHistoryStatus?: StatusEnum;
    CulturalConsiderationsStatus?: StatusEnum;
    DevelopmentalHistoryStatus?: StatusEnum;
    EatingDisorderStatus?: StatusEnum;
    MentalHealthTreatmentHistoryStatus?: StatusEnum;
    CurrentNeedsStatus?: StatusEnum;
    GAFScaleStatus?: StatusEnum;
    TobaccoAndDrugAssessmentStatus?: StatusEnum;
    GoalStatus?: StatusEnum;
    MedicationReconciliationStatus?: StatusEnum;
    CarePlanStatus?: StatusEnum;
    RequestStatus?: StatusEnum;
    TransitionalCareStatus?: StatusEnum;
    EducationStatus?: StatusEnum;
    DocumentEngagementStatus?: StatusEnum;
    FollowUpStatus?: StatusEnum;
    InterventionStatus?: StatusEnum;
    ProgressNoteStatus?: StatusEnum;
    RemoteMonitoringStatus?: StatusEnum;
    HealthPlanStatus?: StatusEnum;
    DemographicStatus?: StatusEnum;
    AllergyStatus?: StatusEnum;
    CareCircleStatus?: StatusEnum;
    TabletStatus?: StatusEnum;
    PcpSpecialistStatus?: StatusEnum;

    // Demographic completion flags
    IsCompletedBasicInfo?: boolean;
    IsCompletedAddressInfo?: boolean;
    IsCompletedContactInfo?: boolean;

    // Member onboarding
    DateOfOnboard?: string;
    MemberStatus?: MemberStatusEnum;

    // Audit (flat access)
    CreatedOn?: string;
    CreatedBy?: string;

    // Workflow checklist flags
    IsCarePlanReviewed?: boolean;
    CarePlanReviewedOn?: string | null;
    IsNotEncounterable?: boolean;
    AddKeyContact?: boolean;
    EngageWithMember?: boolean;
    AddPlanOfCare?: boolean;
    MedicalReconcilation?: boolean;
    PlanDischargeLocation?: boolean;
    IsInTransition?: boolean;

    // HRA
    HraEducationFollowUpRequestIds?: string[];
    EducationMaterialAssigned?: boolean;

    // Misc
    IgnoreSaveSummaryNotes?: boolean;
    IgnoreTimelineInsert?: boolean;
    IsDefaultReasonForVisitChanged?: boolean;
    IsElevatedPHQ9Addressed?: boolean;
    ElevatedPHQ9Notes?: string;
    IsAIFollowupRecommendations?: boolean;
    IsAIProgressNotes?: boolean;
    DiagnosticStatus?: DiagnosticStatus;
    CurrentMenu?: string;
}

export class DocumentEngagement implements IDocumentEngagement {
    Id: string;
    MeansOfEngagement: MeansOfEngagementEnum;
    MemberId: string;
    EngagedProvider: EngagedProvider;
    Notes: string;
    CreationTime: string;
    VisitedTime: string;
    ReasonForVisit: ReasonForVisit;
    StaffReferral: StaffReferral;
    PatientOutreachOrInitiatedType: PatientOutreachOrInitiatedTypeEnum;
    UpdationTime: string;
    UpdatedProvider: EngagedProvider;
    EncounterSubmissionStatus: EncounterSubmissionStatusEnum;
    Contacted: boolean;
    ContactResult: string;
    AvoidedER: boolean;
    TelemedicineDetails: TelemedicineDetails;
    SdohAssessmentId: string;
    VitalsignAssessmentId: string;
    PhqAssessmentId: string;
    PastPresentHistoryId: string;
    PastSurgicalHistoryId: string;
    ReviewSystemId: string;
    PhysicalExamId: string;
    HgbA1CAssessmentId: string;
    LipidAssessmentId: string;
    CounselingAssessmentId: string;
    DiabetesFootAssessmentId: string;
    UrinalysisReportId: string;
    MiniMentalExamId: string;
    SuicidalHomicidalIdeationId: string;
    FactorAndRiskAssessmentId: string;
    SubjectiveAssessmentId: string;
    SummaryNotes: SummaryNote[];
    ProgressNotes: string;
    FollowupRecommendations: string;
    CompletedRequestIds: string[];
    InProgressRequestIds: string[];
    ActiveMedications: Medications[];
    AppointmentIds: string[];
    AssociatedAppointmentId: string;
    FollowUpRequestIds: string[];
    EducationSessionIds: string[];
    AssessedRole: ProviderRole;
    RequestReviewStatus: RequestReviewStatus;
    ConsentFormId: string;
    BudgetFormId: string;
    TransitionalCareId: string;
    AdlAssessmentId: string;
    SexualHealthId: string;
    FallRiskId: string;
    VisionAndHearingId: string;
    AdvancedDirectiveId: string;
    TobaccoAndDrugAssessmentId: string;
    PreventativeHealthAdviceId: string;
    HraEducationId: string;
    MemberName: string;
    IsShowStaffReferral: boolean;
    isShowPatientOutReach: boolean;
    HasClaim: boolean;
    CanReopen?: boolean;
    CareCircleConfirmation: CareCircleConfirmation;
    StatementOfCare: string;

    // New fields for draft/finalized lifecycle
    Type?: DocumentEngagementType;
    DraftWorkflowState?: DraftWorkflowState;
    Encounters?: MemberEncounterDraft[];

    // Additional assessment IDs
    ScreeningFuhFumId?: string;
    ChpFullProgramId?: string;
    HamiltonAnxietyScaleId?: string;
    AbuseHistoryId?: string;
    LegalHistoryId?: string;
    CulturalConsiderationsId?: string;
    DevelopmentalHistoryId?: string;
    EatingDisorderId?: string;
    MentalHealthTreatmentHistoryId?: string;
    CurrentNeedsId?: string;
    GAFScaleId?: string;
    AdditionalCHPCriteriaId?: string;

    // Status fields (flat access)
    SdohAssessmentStatus?: StatusEnum;
    MiniMentalExamStatus?: StatusEnum;
    SuicidalHomicidalIdeationStatus?: StatusEnum;
    VitalSignStatus?: StatusEnum;
    HamiltonAnxietyScaleStatus?: StatusEnum;
    AbuseHistoryStatus?: StatusEnum;
    LegalHistoryStatus?: StatusEnum;
    CulturalConsiderationsStatus?: StatusEnum;
    DevelopmentalHistoryStatus?: StatusEnum;
    EatingDisorderStatus?: StatusEnum;
    MentalHealthTreatmentHistoryStatus?: StatusEnum;
    CurrentNeedsStatus?: StatusEnum;
    GAFScaleStatus?: StatusEnum;
    TobaccoAndDrugAssessmentStatus?: StatusEnum;
    GoalStatus?: StatusEnum;
    MedicationReconciliationStatus?: StatusEnum;
    CarePlanStatus?: StatusEnum;
    RequestStatus?: StatusEnum;
    TransitionalCareStatus?: StatusEnum;
    EducationStatus?: StatusEnum;
    DocumentEngagementStatus?: StatusEnum;
    FollowUpStatus?: StatusEnum;
    InterventionStatus?: StatusEnum;
    ProgressNoteStatus?: StatusEnum;
    RemoteMonitoringStatus?: StatusEnum;
    HealthPlanStatus?: StatusEnum;
    DemographicStatus?: StatusEnum;
    AllergyStatus?: StatusEnum;
    CareCircleStatus?: StatusEnum;
    TabletStatus?: StatusEnum;
    PcpSpecialistStatus?: StatusEnum;

    // Demographic completion flags
    IsCompletedBasicInfo?: boolean;
    IsCompletedAddressInfo?: boolean;
    IsCompletedContactInfo?: boolean;

    // Member onboarding
    DateOfOnboard?: string;
    MemberStatus?: MemberStatusEnum;

    // Audit (flat access)
    CreatedOn?: string;
    CreatedBy?: string;

    // Workflow checklist flags
    IsCarePlanReviewed?: boolean;
    CarePlanReviewedOn?: string | null;
    IsNotEncounterable?: boolean;
    AddKeyContact?: boolean;
    EngageWithMember?: boolean;
    AddPlanOfCare?: boolean;
    MedicalReconcilation?: boolean;
    PlanDischargeLocation?: boolean;
    IsInTransition?: boolean;

    // HRA
    HraEducationFollowUpRequestIds?: string[];
    EducationMaterialAssigned?: boolean;

    // Misc
    IgnoreSaveSummaryNotes?: boolean;
    IgnoreTimelineInsert?: boolean;
    IsDefaultReasonForVisitChanged?: boolean;
    IsElevatedPHQ9Addressed?: boolean;
    ElevatedPHQ9Notes?: string;
    IsAIFollowupRecommendations?: boolean;
    IsAIProgressNotes?: boolean;
    DiagnosticStatus?: DiagnosticStatus;
    CurrentMenu?: string;

    constructor() {
        this.Id = '';
        this.MemberId = '';
    }
}
